import React, { useEffect, useState } from 'react';
import Icon from '../AppIcon';

const Toast = ({ 
  id,
  message, 
  type = 'info', // 'success', 'error', 'warning', 'info'
  duration = 5000,
  onClose
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose?.(id);
    }, 300);
  };

  const getIcon = () => {
    switch (type) {
      case 'success': return 'CheckCircle2';
      case 'error': return 'XCircle';
      case 'warning': return 'AlertTriangle';
      case 'info': return 'Info';
      default: return 'Bell';
    }
  };

  const getColors = () => {
    switch (type) {
      case 'success': 
        return {
          bg: 'bg-green-50 dark:bg-green-900/20',
          border: 'border-green-500',
          icon: 'text-green-600 dark:text-green-400',
          text: 'text-green-900 dark:text-green-100'
        };
      case 'error': 
        return {
          bg: 'bg-red-50 dark:bg-red-900/20',
          border: 'border-red-500',
          icon: 'text-red-600 dark:text-red-400',
          text: 'text-red-900 dark:text-red-100'
        };
      case 'warning': 
        return {
          bg: 'bg-yellow-50 dark:bg-yellow-900/20',
          border: 'border-yellow-500',
          icon: 'text-yellow-600 dark:text-yellow-400',
          text: 'text-yellow-900 dark:text-yellow-100'
        };
      case 'info': 
        return {
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          border: 'border-blue-500',
          icon: 'text-blue-600 dark:text-blue-400',
          text: 'text-blue-900 dark:text-blue-100'
        };
      default: 
        return {
          bg: 'bg-gray-50 dark:bg-gray-900/20',
          border: 'border-gray-500',
          icon: 'text-gray-600 dark:text-gray-400',
          text: 'text-gray-900 dark:text-gray-100'
        };
    }
  };

  if (!isVisible) return null;

  const colors = getColors();

  return (
    <div
      className={`
        ${colors?.bg} ${colors?.border}
        border-l-4 rounded-lg shadow-lg p-4 
        flex items-start gap-3
        transition-all duration-300 ease-in-out
        ${isExiting ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'}
        min-w-[300px] max-w-[400px]
      `}
      role="alert"
    >
      <Icon name={getIcon()} size={20} className={`${colors?.icon} mt-0.5 flex-shrink-0`} />
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${colors?.text} break-words`}>{message}</p>
      </div>
      <button
        onClick={handleClose}
        className={`${colors?.icon} hover:opacity-70 transition-opacity flex-shrink-0`}
        aria-label="Close notification"
      >
        <Icon name="X" size={16} />
      </button>
    </div>
  );
};

// Toast Container Component
export const ToastContainer = ({ toasts, onRemove }) => {
  return (
    <div className="fixed top-4 right-4 z-50 space-y-3 pointer-events-none">
      <div className="pointer-events-auto space-y-3">
        {toasts?.map((toast) => (
          <Toast
            key={toast?.id}
            id={toast?.id}
            message={toast?.message}
            type={toast?.type}
            duration={toast?.duration}
            onClose={onRemove}
          />
        ))}
      </div>
    </div>
  );
};

export default Toast;